import { useEffect, useRef } from 'react';

import { useFocusTrap } from '../../hooks/useFocusTrap.js';
import { formatWeekRange, weekEndOf } from '../../utils/weekDates.js';

export interface PendingAssignment {
  userIds: string[];
  teamId: string;
  teamName: string;
  /** Display name when exactly one person is being assigned. */
  personName: string | null;
  /** Outstanding weeks across everyone in `userIds`, earliest first. */
  weekStartDates: string[];
  /**
   * Whether `weekStartDates` is the complete picture. It comes from a row-capped,
   * possibly-still-loading query, so an empty array can mean "nothing outstanding" or
   * "we do not know yet". This dialog exists precisely to stop a manager inheriting a
   * backlog unawares, so it must never present the second as the first.
   */
  weeksKnown: boolean;
}

interface AssignConfirmDialogProps {
  pending: PendingAssignment | null; // null = closed
  onCancel: () => void;
  onConfirm: () => void;
  isPending: boolean;
}

/**
 * Confirms a team assignment before it commits, naming how many outstanding weeks it
 * will move and how far back they reach.
 *
 * This exists because assignment has no effective dating: picking a team hands the new
 * lead *every* outstanding week that person has, including ones from long before the
 * team existed. That trade was accepted on the condition that the manager is told what
 * they are about to inherit — without this step the only feedback is a success toast
 * after the fact, by which point a year of backlog has already moved.
 */
export function AssignConfirmDialog({
  pending,
  onCancel,
  onConfirm,
  isPending,
}: AssignConfirmDialogProps) {
  const confirmBtnRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Handed to the trap rather than focused separately — see its `initialFocusRef` note.
  useFocusTrap(dialogRef, pending !== null, confirmBtnRef);

  useEffect(() => {
    if (!pending) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [pending, onCancel]);

  if (!pending) return null;

  const weeks = pending.weekStartDates;
  const who =
    pending.personName !== null ? pending.personName : `${String(pending.userIds.length)} people`;
  const first = weeks[0];
  const last = weeks[weeks.length - 1];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="assign-dialog-title"
      className="dialog-overlay"
    >
      <div ref={dialogRef} className="dialog dialog--sm">
        <h2 id="assign-dialog-title">
          Assign {who} to {pending.teamName}?
        </h2>

        {weeks.length === 0 && !pending.weeksKnown ? (
          <p>
            We couldn&rsquo;t confirm how many outstanding weeks {who} has. Assignment is not dated,
            so any older weeks move to {pending.teamName}&rsquo;s approver too.
          </p>
        ) : weeks.length === 0 ? (
          <p>{who} has no outstanding weeks right now, so this only affects weeks from here on.</p>
        ) : (
          <p>
            This hands {pending.teamName}&rsquo;s approver{' '}
            <strong>
              {pending.weeksKnown ? '' : 'at least '}
              {weeks.length} outstanding {weeks.length === 1 ? 'week' : 'weeks'}
            </strong>
            {first !== undefined && last !== undefined && (
              <>
                , reaching back to <strong>{formatWeekRange(first, weekEndOf(first))}</strong>
                {first !== last && (
                  <> and running through {formatWeekRange(last, weekEndOf(last))}</>
                )}
              </>
            )}
            .{' '}
            {pending.weeksKnown
              ? 'Assignment is not dated, so older weeks move too.'
              : 'That count may be incomplete — assignment is not dated, so any older weeks move too.'}
          </p>
        )}

        <div className="aobo-dialog__actions">
          <button
            ref={confirmBtnRef}
            type="button"
            className="btn primary"
            disabled={isPending}
            onClick={onConfirm}
          >
            Assign to {pending.teamName}
          </button>
          <button type="button" className="btn btn--ghost" onClick={onCancel} disabled={isPending}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
